import React, { useEffect, useState, useCallback } from "react";
import { Tags, Plus, Edit2, Save, X, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
interface TaxonomyItem {
  id: string;
  ranch_id: string;
  name: string;
  description: string;
}
interface TaxonomyTabProps {
  endpoint: string;
  label: string;
  activeTab: string;
  tabValue: string;
}
function TaxonomyTab({ endpoint, label, activeTab, tabValue }: TaxonomyTabProps) {
  const [items, setItems] = useState<TaxonomyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addName, setAddName] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await authFetch<{ items: TaxonomyItem[] }>(`/api/taxonomy/${endpoint}`);
      setItems(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error(`[Taxonomy ${label}]`, msg);
    } finally {
      setLoading(false);
    }
  }, [endpoint, label]);
  useEffect(() => {
    if (activeTab === tabValue) {
      fetchItems();
    }
  }, [activeTab, tabValue, fetchItems]);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addName.trim()) {
      toast.error("Name is required");
      return;
    }
    setAdding(true);
    try {
      await authFetch(`/api/taxonomy/${endpoint}`, {
        method: "POST",
        body: JSON.stringify({ name: addName.trim(), description: addDesc.trim() }),
      });
      toast.success(`${label.slice(0, -1)} added`);
      setAddName("");
      setAddDesc("");
      fetchItems();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setAdding(false);
    }
  };
  const startEdit = (item: TaxonomyItem) => {
    setEditId(item.id);
    setEditName(item.name);
    setEditDesc(item.description);
  };
  const handleEditSave = async () => {
    if (!editId || !editName.trim()) return;
    setEditSaving(true);
    try {
      await authFetch(`/api/taxonomy/${endpoint}/${editId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editName.trim(), description: editDesc.trim() }),
      });
      toast.success("Updated");
      setEditId(null);
      fetchItems();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await authFetch(`/api/taxonomy/${endpoint}/${id}`, { method: "DELETE" });
      toast.success("Deleted");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };
  return (
    <div className="space-y-4">
      {/* Add form */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
            <Input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder={`New ${label.slice(0, -1).toLowerCase()} name`}
              className="flex-1"
            />
            <Input
              value={addDesc}
              onChange={(e) => setAddDesc(e.target.value)}
              placeholder="Description (optional)"
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={adding}
              className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90 shrink-0"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </form>
        </CardContent>
      </Card>
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
      )}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
              <Skeleton className="h-5 flex-1" />
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tags}
          title={`No ${label.toLowerCase()} yet`}
          description={`Add your first ${label.slice(0, -1).toLowerCase()} using the form above.`}
        />
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 border rounded-lg bg-card hover:bg-accent/30 transition-colors"
            >
              {editId === item.id ? (
                <div className="flex-1 flex flex-col sm:flex-row gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Description"
                    className="flex-1"
                  />
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      onClick={handleEditSave}
                      disabled={editSaving}
                      className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">{item.name}</p>
                    {item.description && (
                      <p className="text-2xs text-muted-foreground truncate">{item.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => startEdit(item)}
                      aria-label={`Edit ${item.name}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          aria-label={`Delete ${item.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{item.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove this {label.slice(0, -1).toLowerCase()}.
                            Documents using it will retain the reference but it won't appear in dropdowns.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(item.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export function TaxonomyPage() {
  const [activeTab, setActiveTab] = useState("types");
  return (
    <>
      <PageHeader
        title="Taxonomy Management"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Documents", href: "/documents" },
          { label: "Taxonomy" },
        ]}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="types">Types</TabsTrigger>
          <TabsTrigger value="correspondents">Correspondents</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
        </TabsList>
        <TabsContent value="types">
          <TaxonomyTab
            endpoint="types"
            label="Types"
            activeTab={activeTab}
            tabValue="types"
          />
        </TabsContent>
        <TabsContent value="correspondents">
          <TaxonomyTab
            endpoint="correspondents"
            label="Correspondents"
            activeTab={activeTab}
            tabValue="correspondents"
          />
        </TabsContent>
        <TabsContent value="tags">
          <TaxonomyTab
            endpoint="tags"
            label="Tags"
            activeTab={activeTab}
            tabValue="tags"
          />
        </TabsContent>
        <TabsContent value="storage">
          <TaxonomyTab
            endpoint="storage-locations"
            label="Storage Locations"
            activeTab={activeTab}
            tabValue="storage"
          />
        </TabsContent>
      </Tabs>
    </>
  );
}